import tw from "twin.macro"
import { FeaturedSnippetModuleProps } from "./FeaturedSnippetModule.d"
import FeaturedSnippetPortableText from "./FeaturedSnippetPortableText"
import FeaturedSnippetCTAs from "./FeaturedSnippetCTAs"
import FeaturedSnippetVideo from "./FeaturedSnippetVideo"
import FeaturedSnippetTable from "./FeaturedSnippetTable"
import FeaturedSnippetImage from "./FeaturedSnippetImage"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import { useContext, useEffect, useRef } from "react"
import { TealiumContext } from "../../../contexts/Tealium"

// Whitelist of allowed variable names
const allowedTealiumKeys = [
  "page_url",
  "campaign_name",
  "vehicle_model",
  "vehicle_exterior_color",
  "vehicle_year",
  "vehicle_interior_color",
  "vehicle_trim",
  "vehicle_trim_id",
  "vehicle_vin",
  "vehicle_sale_price",
  "accessory_name",
  "button_location",
  "allocated_dealer_name",
  "allocated_dealer_code",
  "vehicle_avail",
  "initial_referrer",
  "page_language",
  "page_type",
  "vehicle_segment_vehicle_page",
  "vehicle_model_truncated",
  "customer_ip",
  "geolocation_zip",
  "video_type",
]

// Function to filter tealDataFromView as per the SDR specifications ( 0.1-F3a )
const filterTealData = (data: Record<string, any>) => {
  return Object.keys(data)
    .filter(key => allowedTealiumKeys.includes(key))
    .reduce(
      (filteredData, key) => {
        filteredData[key] = data[key]
        return filteredData
      },
      {} as Record<string, any>,
    )
}

const renderContentItem = (
  title: string | null,
  contentItem: any,
  column: number,
  tealDataFromView?: object,
) => {
  switch (contentItem._type) {
    case "featSnippetPortableText":
      return (
        <FeaturedSnippetPortableText key={contentItem._key} {...contentItem} />
      )
    case "ctas":
      return <FeaturedSnippetCTAs key={contentItem._key} {...contentItem} />
    case "featSnippetVideo":
      return (
        <FeaturedSnippetVideo
          key={contentItem._key}
          title={title}
          {...contentItem}
          styles={
            column == 1
              ? tw`min-h-[200px] lg:min-h-[756px]`
              : tw`min-h-[200px] lg:min-h-[400px]`
          }
          tealDataFromView={tealDataFromView}
        />
      )
    case "featSnippetTable":
      return <FeaturedSnippetTable key={contentItem._key} {...contentItem} />
    case "featSnippetImg":
      return (
        <FeaturedSnippetImage
          key={contentItem._key}
          fromHybridSegment="true"
          {...contentItem}
        />
      )
    default:
      return null
  }
}

const FeaturedSnippetModule: React.FC<FeaturedSnippetModuleProps> = ({
  _key,
  _type,
  layout,
  backgroundColorString = "white",
  title,
  singleColContent,
  doubleColContent,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  //tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData, tealDataFromView } = useContext(TealiumContext)

  // Filter tealDataFromView to only include allowed keys as per SDR specifications
  const filteredTealData = tealDataFromView
    ? filterTealData(tealDataFromView)
    : {}

  useEffect(() => {
    if (containerRef.current && tealPageData && filteredTealData) {
      // Find all <a> tags within the component
      const links = containerRef.current.getElementsByTagName("a")
      // Add analytics-id attribute to each link
      Array.from(links).forEach((link, index) => {
        link.setAttribute(
          "analytics-id",
          `link:${title || "Featured Snippet Module"}:${index}`,
        )
        link.onclick = () => {
          trackTealEvent({
            tealium_event: "cta_click",
            coupon_module_text: link.innerText,
            link_href: link.href,
            ...(filteredTealData && { ...filteredTealData }),
          })
        }
      })
    }
  }, [title, tealPageData, filteredTealData])

  return (
    <section
      ref={containerRef}
      key={_key}
      css={[
        tw`w-full py-10 px-4 lg:py-20`,
        backgroundColorString === "grey" ? tw`bg-mystic` : tw`bg-white`,
      ]}
    >
      <div css={[tw`max-w-screen-xl mx-auto`]}>
        {layout === "1" && singleColContent && filteredTealData && (
          <div css={tw`w-full flex flex-col  gap-y-5`}>
            {singleColContent.content?.map(contentItem => (
              <div css={[tw`h-full`]} key={contentItem._key}>
                {renderContentItem(
                  title || "Featured Snippet Module",
                  contentItem,
                  1,
                  filteredTealData,
                )}
              </div>
            ))}
          </div>
        )}
        {layout === "2" && doubleColContent && filteredTealData && (
          <div
            css={tw`grid grid-cols-1 px-4 lg:(grid-cols-2 items-center) h-full`}
          >
            <div css={tw`w-full flex flex-col gap-y-5`}>
              {doubleColContent.colOne?.map(contentItem => (
                <div css={[tw`h-full`]} key={contentItem._key}>
                  {renderContentItem(
                    title || "Featured Snippet Module",
                    contentItem,
                    2,
                    filteredTealData,
                  )}
                </div>
              ))}
            </div>
            <div css={tw`w-full flex flex-col  gap-y-5`}>
              {doubleColContent.colTwo?.map(contentItem => (
                <div css={[tw`h-full`]} key={contentItem._key}>
                  {renderContentItem(
                    title || "Featured Snippet Module",
                    contentItem,
                    2,
                    filteredTealData,
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default FeaturedSnippetModule
